import type { SessionStore, SessionUser } from "../api/index.js";
import { ApiError, toRecoveryAction, type RecoveryAction, type ToRecoveryOptions } from "../recovery/index.js";
import type { AuthApi } from "./auth_api.js";
import { canResend, DEAD_OTP_CODES, normalizeOtp, otpFailure, otpIssued } from "./otp_step.js";
import { isValidEmail, isValidPassword } from "./validation.js";

/**
 * Reset-password step machine (W-1), parity with the app:
 *
 *   email ── sendResetPasswordOtpEmail ─┬─ password account ─▶ otp ── resetWithOtp (check) ─▶ password
 *                                       ├─ google.com / apple.com ─▶ provider ("Sign in with …")
 *                                       └─ no account ─────────────▶ unknown ("Create account")
 *   password ── resetWithOtp (new password) ─▶ loginWithOtp (cookie) ─▶ done
 *
 * The OTP code is kept in memory only, for the reset → login chain (the
 * backend allows that chain for 10 minutes after the check / reset).
 */

export type ResetStep = "email" | "otp" | "password" | "provider" | "unknown" | "done";
export type ResetField = "email" | "otp" | "password";

export interface ResetState {
  step: ResetStep;
  busy: boolean;
  email: string;
  /** provider step: "google.com" | "apple.com" | other non-password provider. */
  providerId?: string;
  otpId?: string;
  resendAvailableAt?: number;
  otpResetCount: number;
  error?: RecoveryAction;
  fieldErrors: Partial<Record<ResetField, string>>;
  user?: SessionUser;
}

export interface ResetDeps {
  api: AuthApi;
  session: SessionStore;
  initialEmail?: string;
  now?: () => number;
  recovery?: ToRecoveryOptions;
}

export interface ResetFlow {
  getState(): ResetState;
  subscribe(cb: () => void): () => void;
  validateField(field: "email" | "password", value: string): void;
  submitEmail(email: string): Promise<void>;
  submitOtp(code: string): Promise<void>;
  submitPassword(password: string): Promise<void>;
  resendOtp(): Promise<void>;
  back(): void;
  dismissError(): void;
}

function isNoUserDoc(err: unknown): boolean {
  return err instanceof ApiError && err.status === 404;
}

export function createResetPasswordFlow(deps: ResetDeps): ResetFlow {
  const now = deps.now ?? Date.now;
  let state: ResetState = {
    step: "email",
    busy: false,
    email: deps.initialEmail?.trim().toLowerCase() ?? "",
    otpResetCount: 0,
    fieldErrors: {},
  };
  /** The verified code, memory only. */
  let code: string | null = null;
  const subs = new Set<() => void>();

  const set = (patch: Partial<ResetState>) => {
    state = { ...state, ...patch };
    for (const s of subs) s();
  };
  const fail = (err: unknown) => set({ busy: false, error: toRecoveryAction(err, deps.recovery) });
  const guardBusy = () => {
    if (state.busy) return true;
    set({ busy: true, error: undefined });
    return false;
  };
  const validateEmail = (v: string) => (isValidEmail(v) ? undefined : "auth.validation.emailInvalid");
  const validatePassword = (v: string) => (isValidPassword(v) ? undefined : "auth.validation.passwordTooShort");

  const send = async (email: string, stayOnOtp: boolean) => {
    const res = await deps.api.sendResetPasswordOtpEmail(email);
    if (res.isSent && res.otpId) {
      set({
        busy: false,
        step: "otp",
        ...otpIssued(res.otpId, now()),
        ...(stayOnOtp ? { otpResetCount: state.otpResetCount + 1 } : {}),
      });
      return;
    }
    if (res.providerId && res.providerId !== "password") {
      set({ busy: false, step: "provider", providerId: res.providerId });
      return;
    }
    set({ busy: false, step: "unknown" });
  };

  return {
    getState: () => state,
    subscribe(cb) {
      subs.add(cb);
      return () => {
        subs.delete(cb);
      };
    },

    validateField(field, value) {
      const message = field === "email" ? validateEmail(value.trim()) : validatePassword(value);
      set({ fieldErrors: { ...state.fieldErrors, [field]: message } });
    },

    async submitEmail(raw) {
      const email = raw.trim().toLowerCase();
      const message = validateEmail(email);
      if (message) {
        set({ email, fieldErrors: { email: message } });
        return;
      }
      if (guardBusy()) return;
      set({ email, fieldErrors: {} });
      try {
        await send(email, false);
      } catch (err) {
        // A Firebase account without a KoverNow user: same as no account.
        if (isNoUserDoc(err)) {
          set({ busy: false, step: "unknown" });
          return;
        }
        fail(err);
      }
    },

    async submitOtp(raw) {
      const otp = normalizeOtp(raw);
      if (!otp) {
        set({ fieldErrors: { otp: "auth.validation.otpLength" } });
        return;
      }
      if (!state.otpId || guardBusy()) return;
      set({ fieldErrors: {} });
      try {
        await deps.api.resetWithOtp({ otpId: state.otpId, otp });
        code = otp;
        set({ busy: false, step: "password" });
      } catch (err) {
        set(otpFailure(err, state, deps.recovery));
      }
    },

    async submitPassword(password) {
      const message = validatePassword(password);
      if (message) {
        set({ fieldErrors: { password: message } });
        return;
      }
      if (!state.otpId || !code || guardBusy()) return;
      set({ fieldErrors: {} });
      const otpId = state.otpId;
      try {
        await deps.api.resetWithOtp({ otpId, otp: code, password });
      } catch (err) {
        const errorCode = err instanceof ApiError ? err.problem?.errorCode ?? "" : "";
        if (DEAD_OTP_CODES.has(errorCode)) {
          // Took too long (or too many tries): back to a fresh code.
          code = null;
          set({ ...otpFailure(err, state, deps.recovery), step: "otp" });
          return;
        }
        fail(err);
        return;
      }
      try {
        const res = await deps.api.loginWithOtp(otpId, code);
        code = null;
        deps.session.setSession(res.token, res.user);
        set({ busy: false, step: "done", user: res.user, error: undefined });
      } catch {
        // The password did change: send them to sign in with it.
        code = null;
        set({
          busy: false,
          error: {
            errorCode: "reset_signin_failed",
            messageKey: "errors.reset_signin_failed",
            action: "sign_in",
            kind: "recoverable",
          },
        });
      }
    },

    async resendOtp() {
      const dead = state.error?.action === "resend_code";
      if (!state.otpId || (!dead && !canResend(state, now())) || guardBusy()) return;
      try {
        await send(state.email, true);
      } catch (err) {
        fail(err);
      }
    },

    back() {
      code = null;
      set({
        step: "email",
        busy: false,
        error: undefined,
        fieldErrors: {},
        otpId: undefined,
        resendAvailableAt: undefined,
        providerId: undefined,
      });
    },

    dismissError() {
      set({ error: undefined });
    },
  };
}
